import router from './router'
import { useAppStoreWithOut } from '@/store/modules/app'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { NO_REDIRECT_WHITE_LIST } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { ref } from 'vue'
import { getUserInfoApi } from './api/login'
import { logError, logWarning, sendEvent } from '@/utils/tracker'

const { start, done } = useNProgress()

const { loadStart, loadDone } = usePageLoading()

const showProgress = ref(false)

async function syncUserInfo(userStore: ReturnType<typeof useUserStoreWithOut>) {
  let permissions = userStore.getPermissions
  try {
    const res = await getUserInfoApi()
    if (!res?.data) {
      return permissions
    }
    permissions = (res.data['permissions'] as Record<string, string[]>) || permissions
    userStore.setPermissions(permissions)
    userStore.setHeadImg((res.data['head_img'] as string) || '')
    userStore.setStoreName((res.data['store_name'] as string) || '')
    userStore.setUserName((res.data['username'] as string) || '')
    userStore.setConfig((res.data['config'] as any) || {})
    userStore.setExpire((res.data['expire'] as string) || '')
    userStore.setDataAuth(res.data['data_auth'] as string)
    userStore.setStoreOptions(res.data['store_options'] as any[])
    userStore.setTableColumns(res.data['table_columns'] as Record<string, any[]>)
  } catch (error) {
    // 接口失败时：401 等会清空登录态；其它错误沿用本地缓存权限，避免刷新白屏
    console.error('获取用户信息失败', error)
    logWarning('sync_user_info_failed', {
      error_message: (error as Error)?.message ?? String(error)
    })
  }
  return permissions
}

router.beforeEach(async (to, from, next) => {
  if (to.name !== 'Login') {
    // const configStore = useConfigStore()
    // configStore.inSureConfig()
  }
  if (showProgress.value) {
    start()
  }
  loadStart()
  const permissionStore = usePermissionStoreWithOut()
  const appStore = useAppStoreWithOut()
  const userStore = useUserStoreWithOut()
  try {
    if (userStore.getUserInfo) {
      if (to.path === '/login') {
        next({ path: '/' })
        return
      }
      if (permissionStore.getIsAddRouters) {
        next()
        return
      }

      // 开发者可根据实际情况进行修改
      const roleRouters = []
      // 是否使用动态路由
      if (appStore.getDynamicRouter) {
        const permissions = await syncUserInfo(userStore)
        // 同步用户信息过程中若被登出（如 401），跳转登录页
        if (!userStore.getUserInfo) {
          next(`/login?redirect=${to.path}`)
          return
        }
        appStore.serverDynamicRouter
          ? await permissionStore.generateRoutes(
              'server',
              roleRouters as AppCustomRouteRecordRaw[],
              permissions
            )
          : await permissionStore.generateRoutes('frontEnd', roleRouters as string[], permissions)
      } else {
        await permissionStore.generateRoutes('static')
      }

      permissionStore.getAddRouters.forEach((route) => {
        router.addRoute(route as unknown as RouteRecordRaw) // 动态添加可访问路由表
      })
      const redirectPath = from.query.redirect || to.path
      const redirect = decodeURIComponent(redirectPath as string)
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect }
      permissionStore.setIsAddRouters(true)
      next(nextData)
    } else {
      if (NO_REDIRECT_WHITE_LIST.indexOf(to.path) !== -1) {
        next()
      } else {
        next(`/login?redirect=${to.path}`) // 否则全部重定向到登录页
      }
    }
  } catch (error) {
    console.error('路由守卫异常', error)
    // 守卫异常会导致白屏/回登录页，属高优先级问题，必须能在线上看到
    logError('router_guard_error', {
      path: to.path,
      error_message: (error as Error)?.message ?? String(error),
      stack: (error as Error)?.stack ?? ''
    })
    // 兜底：首次动态路由失败时回登录页，避免整页白屏
    if (!permissionStore.getIsAddRouters) {
      userStore.setUserInfo(undefined)
      next(`/login?redirect=${to.path}`)
    } else {
      next(false)
    }
  }
})

router.afterEach((to, from) => {
  useTitle(to?.meta?.title as string)
  if (showProgress.value) {
    done() // 结束Progress
  }
  loadDone()
  // 页面访问（PV）：一处覆盖全站所有页面，无需逐页埋点
  sendEvent('page_view', {
    path: to.path,
    name: String(to.name ?? ''),
    title: String(to.meta?.title ?? ''),
    from: from.path
  })
})
